import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  AiAgentsTabId,
  AiAgentsUrlParams,
  buildAiAgentsSearch,
  parseAiAgentsTab,
  patchAiAgentsSearch,
  readAiAgentsParam,
} from './aiAgentsModel';

export interface IAiAgentsWriteOptions {
  /**
   * `true` — шаг в истории: «Назад» его отменит (открыть прогон, тред,
   * документ). По умолчанию `replace` — правка фильтра или смена вкладки не
   * засоряет историю (как у «Маркетинга»).
   */
  push?: boolean;
}

export interface IAiAgentsDeepLink {
  /** Открытая вкладка; незнакомая в адресе — «Обзор». */
  tab: AiAgentsTabId;
  /** Параметр вкладки из адреса (`?run=12` → `'12'`); нет — `null`. */
  param: (name: string) => string | null;
  /**
   * Сменить вкладку. Параметры прежней вкладки снимаются; `params` —
   * параметры новой (обзор → «Uruchomienia» с открытым прогоном).
   */
  setTab: (
    tab: AiAgentsTabId,
    params?: AiAgentsUrlParams,
    options?: IAiAgentsWriteOptions,
  ) => void;
  /** Поправить параметры текущей вкладки; `null` — снять параметр. */
  setParams: (
    patch: AiAgentsUrlParams,
    options?: IAiAgentsWriteOptions,
  ) => void;
}

/**
 * ТЗ 0104 — состояние страницы «ИИ-агент» живёт в адресе (как у
 * `useMarketingDeepLink`): ссылку на «прогон 12» можно отправить коллеге, а
 * перезагрузка показывает то же самое.
 *
 * Хук общий для всех вкладок: какие параметры бывают, знает сама вкладка
 * (`run`, `thread`, `doc`…), — новой вкладке не нужно править этот файл.
 * Параметр `tab` занят: вкладка не может его переписать.
 */
export const useAiAgentsDeepLink = (): IAiAgentsDeepLink => {
  const history = useHistory();
  const { search, pathname } = useLocation();

  const tab = useMemo(() => parseAiAgentsTab(search), [search]);

  const param = useCallback((name: string) => readAiAgentsParam(search, name), [
    search,
  ]);

  const go = useCallback(
    (nextSearch: string, push: boolean) => {
      const target = { pathname, search: nextSearch };
      if (push) history.push(target);
      else history.replace(target);
    },
    [history, pathname],
  );

  const setTab = useCallback(
    (
      next: AiAgentsTabId,
      params?: AiAgentsUrlParams,
      { push = false }: IAiAgentsWriteOptions = {},
    ) => {
      if (next === tab && !params) return;
      go(buildAiAgentsSearch(next, params), push);
    },
    [go, tab],
  );

  const setParams = useCallback(
    (patch: AiAgentsUrlParams, { push = false }: IAiAgentsWriteOptions = {}) =>
      go(patchAiAgentsSearch(search, patch), push),
    [go, search],
  );

  return useMemo(() => ({ tab, param, setTab, setParams }), [
    tab,
    param,
    setTab,
    setParams,
  ]);
};
